import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";


const BASE_URL =
  process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

// Helper function to get token
const getToken = () => {
  try {
    // Check localStorage first
    const localData = localStorage.getItem("loggedUser");
    if (localData) {
      const parsed = JSON.parse(localData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }

    // Check sessionStorage
    const sessionData = sessionStorage.getItem("user");
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }

    return null;
  } catch (error) {
    console.error("Error getting token:", error);
    return null;
  }
};

// Fetch orders
export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async ({page = 1, search = "", archived = false, date_from = "", date_to = "", payment_status = "", order_status = "", type = ""}) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      let url = `${BASE_URL}/dashboard/orders?page=${page}&search=${encodeURIComponent(search)}`;
      if (archived) url += "&archived=1";
      if (date_from) url += `&date_from=${date_from}`;
      if (date_to) url += `&date_to=${date_to}`;
      if (payment_status) url += `&payment_status=${payment_status}`;
      if (order_status) url += `&status=${order_status}`; // ✅ مؤكد: الباك في OrderRepository يقرأ $request->status
      if (type) url += `&type=${type}`; // ✅ فلتر نوع الطلب: code أو book

      const response = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Orders API Error:", error);
      throw error;
    }
  }
);

// Update order status
export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ id, status }) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/orders/${id}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Update Order Status API Error:", error);
      throw error;
    }
  }
);

// Update order payment status
export const updateOrderPaymentStatus = createAsyncThunk(
  "orders/updateOrderPaymentStatus",
  async ({ id, paymentStatus }) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/orders/${id}/payment-status`,
        { payment_status: paymentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Update Order Payment Status API Error:", error);
      throw error;
    }
  }
);

// Delete order
export const deleteOrder = createAsyncThunk(
  "orders/deleteOrder",
  async (id) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(
        `${BASE_URL}/dashboard/orders/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Delete Order API Error:", error);
      // toast.error(error.response.data.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  }
);

// Get single order
export const getOrder = createAsyncThunk("orders/getOrder", async (id) => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await axios.get(`${BASE_URL}/dashboard/orders/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Get Order API Error:", error);
    throw error;
  }
});

// Archive single order
export const archiveOrder = createAsyncThunk(
  "orders/archiveOrder",
  async ({ id, reason }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/dashboard/orders/${id}/archive`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

// Archive orders by date
export const archiveOrdersByDate = createAsyncThunk(
  "orders/archiveOrdersByDate",
  async ({ date, reason }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/dashboard/orders/archive-by-date`,
        { date, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

// Restore archived order
export const restoreOrder = createAsyncThunk(
  "orders/restoreOrder",
  async (id) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/dashboard/orders/${id}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

const orderSlice = createSlice({
  name: "orders",
  initialState: {
    orders: [],
    meta: null,
    loading: false,
    error: null,
    success: null,
    currentOrder: null,
  },
  reducers: {
    clearState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = null;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data;
        state.meta = action.payload.meta;
        state.error = null;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update order status
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم تحديث حالة الطلب بنجاح";
        state.error = null;
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update order payment status
      .addCase(updateOrderPaymentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderPaymentStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم تحديث حالة الدفع بنجاح";
        state.error = null;
      })
      .addCase(updateOrderPaymentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete order
      .addCase(deleteOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم حذف الطلب بنجاح";
        state.error = null;
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get single order
      .addCase(getOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload.data;
        state.error = null;
      })
      .addCase(getOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Archive order
      .addCase(archiveOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم أرشفة الطلب بنجاح";
        state.error = null;
      })
      .addCase(archiveOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Archive orders by date
      .addCase(archiveOrdersByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveOrdersByDate.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم أرشفة الطلبات بنجاح";
        state.error = null;
      })
      .addCase(archiveOrdersByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Restore order
      .addCase(restoreOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم استعادة الطلب بنجاح";
        state.error = null;
      })
      .addCase(restoreOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearState, clearCurrentOrder } = orderSlice.actions;
export const fetchOrder = getOrder;
export default orderSlice.reducer;

